import { useState, useEffect } from 'react';
import { getMakes, createMake, updateMake, deleteMake } from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardHeader, CardContent, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Pencil, Trash2, Plus, X, AlertCircle } from 'lucide-react';

export default function MakesAdmin() {
  const [makes, setMakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(null);

  useEffect(() => {
    setLoading(true);
    getMakes().then(setMakes).finally(() => setLoading(false));
  }, []);

  const handleAdd = async () => {
    if (!name.trim()) return;
    try {
      const newMake = await createMake({ name: name.trim() });
      setMakes([...makes, newMake]);
      setName('');
    } catch (err) {
      alert(err.error || 'Failed to add make');
    }
  };

  const handleUpdate = async (id) => {
    try {
      const updated = await updateMake(id, { name, slug: name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-') });
      setMakes(makes.map(m => m._id === id ? updated : m));
      setEditing(null);
      setName('');
    } catch (err) {
      alert(err.error || 'Failed to update make');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    try {
      await deleteMake(deleteDialog._id);
      setMakes(makes.filter(m => m._id !== deleteDialog._id));
    } catch (err) {
      alert('Failed to delete make');
    } finally {
      setDeleteDialog(null);
    }
  };

  const startEdit = (make) => {
    setEditing(make._id);
    setName(make.name);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Makes</h1>

      {/* Add/Edit Form */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{editing ? 'Edit Make' : 'Add New Make'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              placeholder="Make name (e.g., Honda)"
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && (editing ? handleUpdate(editing) : handleAdd())}
              className="max-w-md"
            />
            {editing ? (
              <Button onClick={() => handleUpdate(editing)}>Update</Button>
            ) : (
              <Button onClick={handleAdd} variant="default">
                <Plus className="w-4 h-4 mr-2" />
                Add
              </Button>
            )}
            {editing && (
              <Button variant="outline" onClick={() => { setEditing(null); setName(''); }}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40%]">Name</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8 text-sm text-slate-400">
                  Loading...
                </TableCell>
              </TableRow>
            ) : makes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8 text-sm text-slate-400">
                  No makes yet.
                </TableCell>
              </TableRow>
            ) : (
              makes.map(m => (
                <TableRow key={m._id}>
                  <td className="font-medium text-slate-900">{m.name}</td>
                  <td className="font-mono text-sm text-slate-500">{m.slug}</td>
                  <td className="text-right">
                    {!editing || editing !== m._id ? (
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-indigo-600 hover:text-indigo-700"
                          onClick={() => startEdit(m)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                          onClick={() => setDeleteDialog(m)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-indigo-600 font-medium">Editing...</span>
                    )}
                  </td>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Delete Confirmation Dialog */}
      {deleteDialog && (
        <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="w-5 h-5" />
                Delete Make
              </DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{deleteDialog.name}"? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog(null)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}